import { useMemo, Fragment } from "react";
import {
  differenceInCalendarDays, parseISO, addDays, format, startOfWeek,
  startOfMonth, startOfQuarter, startOfYear, addMonths, addQuarters, addYears,
} from "date-fns";
import { phaseColor, HEALTH_COLORS, HEALTH_LABELS, MS_SHAPES } from "@/lib/constants";
import { Map, Star } from "lucide-react";

const LABEL_W = 168;
const HEADER_H = 40;
const LANE_PAD = 12;
const ROW_H = 40;
const BAR_H = 24;

const isLaunch = (t) => t.is_launch || /launch/i.test(t.title || "");

function packRows(items) {
  const sorted = [...items].sort((a, b) => a.startOff - b.startOff);
  const rowEnds = [];
  const placed = [];
  sorted.forEach((it) => {
    let r = rowEnds.findIndex((end) => it.startOff >= end);
    if (r === -1) { r = rowEnds.length; rowEnds.push(0); }
    rowEnds[r] = it.packEnd;
    placed.push({ ...it, row: r });
  });
  return { placed, rowCount: Math.max(rowEnds.length, 1) };
}

function buildTicks(min, totalDays, unit) {
  const ticks = [];
  const end = addDays(min, totalDays);
  const push = (d, label) => ticks.push({ off: Math.max(0, differenceInCalendarDays(d, min)), label });
  if (unit === "day") {
    for (let d = 0; d < totalDays; d += 1) ticks.push({ off: d, label: format(addDays(min, d), "d") });
  } else if (unit === "week") {
    for (let d = 0; d < totalDays; d += 7) ticks.push({ off: d, label: format(addDays(min, d), "MMM d") });
  } else if (unit === "month") {
    let c = startOfMonth(min);
    while (c < end) { push(c, format(c, "MMM yy")); c = addMonths(c, 1); }
  } else if (unit === "quarter") {
    let c = startOfQuarter(min);
    while (c < end) { push(c, format(c, "QQQ yyyy")); c = addQuarters(c, 1); }
  } else if (unit === "year") {
    let c = startOfYear(min);
    while (c < end) { push(c, format(c, "yyyy")); c = addYears(c, 1); }
  }
  return ticks;
}

export default function RoadmapSwimlane({ tasks, onOpenTask, pxPerDay: pxPerDayProp = 26, tickUnit = "week", summarized = false, colorFor, bodyColorFor, timescaleColor = "#132A5E", fontColor = "", fontSize = 11, holidays = {}, hideDates = false, labelWrap = 200, storyIds = {}, laneOrder = [], onMoveLane }) {
  const colorOf = colorFor || ((phase, idx) => phaseColor(phase, idx));
  const bodyOf = bodyColorFor || colorOf;

  const visible = useMemo(() => {
    if (!summarized) return tasks;
    return tasks.filter((t) => isLaunch(t) || (t.is_milestone && t.is_critical));
  }, [tasks, summarized]);

  const saneMax = useMemo(() => { const d = new Date(); d.setFullYear(d.getFullYear() + 15); return d; }, []);
  const saneMin = useMemo(() => new Date(1990, 0, 1), []);
  const allDated = useMemo(() => visible.filter((t) => t.start_date && t.end_date), [visible]);
  const dated = useMemo(() => allDated.filter((t) => {
    const s = parseISO(t.start_date), e = parseISO(t.end_date);
    return s >= saneMin && e <= saneMax;
  }), [allDated, saneMin, saneMax]);
  const outliers = useMemo(() => allDated.filter((t) => !dated.includes(t)), [allDated, dated]);

  const phaseList = useMemo(() => {
    const seen = [];
    visible.forEach((t) => { const p = t.phase || "Unphased"; if (!seen.includes(p)) seen.push(p); });
    if (laneOrder.length) {
      seen.sort((a, b) => {
        const ia = laneOrder.indexOf(a), ib = laneOrder.indexOf(b);
        return (ia === -1 ? 1e9 : ia) - (ib === -1 ? 1e9 : ib);
      });
    }
    return seen;
  }, [visible, laneOrder]);

  const range = useMemo(() => {
    if (!dated.length) return null;
    let min = parseISO(dated[0].start_date);
    let max = parseISO(dated[0].end_date);
    dated.forEach((t) => {
      const s = parseISO(t.start_date), e = parseISO(t.end_date);
      if (s < min) min = s;
      if (e > max) max = e;
    });
    min = startOfWeek(min, { weekStartsOn: 1 });
    const total = differenceInCalendarDays(max, min) + 6;
    return { min, total };
  }, [dated]);

  // Guard: keep the chart under ~24k px wide no matter how wide the date range is
  const pxPerDay = range && range.total * pxPerDayProp > 24000
    ? Math.max(24000 / range.total, 0.2)
    : pxPerDayProp;

  const lanes = useMemo(() => {
    if (!range) return { lanes: [], totalH: 0 };
    const out = [];
    let y = HEADER_H;
    phaseList.forEach((phase, idx) => {
      const items = dated.filter((t) => (t.phase || "Unphased") === phase).map((t) => {
        const s = parseISO(t.start_date);
        const startOff = differenceInCalendarDays(s, range.min);
        const len = differenceInCalendarDays(parseISO(t.end_date), s) + 1;
        const labelDays = Math.ceil((Math.min((t.title || "").length * 6.5, labelWrap) + 50) / pxPerDay);
        const packEnd = startOff + (t.is_milestone ? 0 : len) + labelDays;
        return { t, startOff, len, packEnd };
      });
      if (!items.length) return;
      const { placed, rowCount } = packRows(items);
      const laneH = rowCount * ROW_H + LANE_PAD * 2;
      out.push({ phase, color: colorOf(phase, idx), bodyColor: bodyOf(phase, idx), top: y, height: laneH, placed });
      y += laneH;
    });
    return { lanes: out, totalH: y };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, dated, phaseList, pxPerDay, colorOf, bodyOf]);

  if (!dated.length) {
    return (
      <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="swimlane-empty">
        <Map className="h-8 w-8 text-primary mx-auto mb-3" />
        <p className="text-muted-foreground">{summarized ? "No launches or critical milestones to summarize yet." : "Add tasks with start & end dates to see the swimlane roadmap."}</p>
      </div>
    );
  }

  const ticks = buildTicks(range.min, range.total, tickUnit);
  const spaced = [];
  ticks.forEach((tk) => {
    const px = tk.off * pxPerDay;
    if (spaced.length && px - spaced[spaced.length - 1].off * pxPerDay < 34) spaced[spaced.length - 1] = tk;
    else spaced.push(tk);
  });
  const chartW = range.total * pxPerDay;

  return (
    <div className="border border-border rounded-lg overflow-hidden" data-testid="roadmap-swimlane">
      <div className="overflow-x-auto">
        <div style={{ width: LABEL_W + chartW, minWidth: "100%" }}>
          <div className="flex" style={{ height: lanes.totalH }}>
            {/* Swimlane labels */}
            <div className="shrink-0 border-r border-border" style={{ width: LABEL_W }}>
              <div style={{ height: HEADER_H, background: timescaleColor }} className="flex items-center px-4 border-b border-border">
                <span className="text-xs font-semibold text-white/90">PHASE / SWIMLANE</span>
              </div>
              {lanes.lanes.map((ln, li) => (
                <div key={ln.phase} className="group/lane flex items-center gap-2 px-3 border-b border-border" style={{ height: ln.height, background: `${ln.color}0d` }}>
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: ln.color }} />
                  <span className="font-bold leading-tight flex-1 min-w-0" style={{ color: fontColor || ln.color, fontSize: fontSize + 2 }} data-testid={`swimlane-label-${ln.phase}`}>{ln.phase}</span>
                  {onMoveLane && (
                    <span className="flex flex-col shrink-0 opacity-0 group-hover/lane:opacity-100 transition-opacity">
                      <button type="button" disabled={li === 0} onClick={() => onMoveLane(ln.phase, -1, lanes.lanes.map((x) => x.phase))}
                        title="Move lane up" className="p-0.5 rounded hover:bg-black/10 text-muted-foreground disabled:opacity-20" data-testid={`lane-up-${ln.phase}`}>
                        <svg width="10" height="8" viewBox="0 0 10 8"><path d="M5 1 L9 7 H1 Z" fill="currentColor" /></svg>
                      </button>
                      <button type="button" disabled={li === lanes.lanes.length - 1} onClick={() => onMoveLane(ln.phase, 1, lanes.lanes.map((x) => x.phase))}
                        title="Move lane down" className="p-0.5 rounded hover:bg-black/10 text-muted-foreground disabled:opacity-20" data-testid={`lane-down-${ln.phase}`}>
                        <svg width="10" height="8" viewBox="0 0 10 8"><path d="M5 7 L1 1 H9 Z" fill="currentColor" /></svg>
                      </button>
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Timeline canvas */}
            <div className="relative" style={{ width: chartW, height: lanes.totalH }}>
              <div className="absolute top-0 left-0 right-0 border-b border-border" style={{ height: HEADER_H, background: timescaleColor }} data-testid="swimlane-timescale-header">
                {spaced.map((w) => (
                  <div key={w.off} className="absolute top-0 h-full border-l border-white/20 text-white/85 px-1 pt-2 whitespace-nowrap" style={{ left: w.off * pxPerDay, fontSize }}>{w.label}</div>
                ))}
              </div>
              {spaced.map((w) => (
                <div key={w.off} className="absolute border-l border-border/30" style={{ left: w.off * pxPerDay, top: HEADER_H, bottom: 0 }} />
              ))}
              {lanes.lanes.map((ln) => (
                <div key={ln.phase} className="absolute left-0 right-0 border-b border-border" style={{ top: ln.top, height: ln.height, background: `${ln.bodyColor}12` }} data-testid={`swimlane-body-${ln.phase}`} />
              ))}

              {lanes.lanes.map((ln) => ln.placed.map(({ t, startOff, len, row }) => {
                const x = startOff * pxPerDay;
                const yTop = ln.top + LANE_PAD + row * ROW_H + (ROW_H - BAR_H) / 2;
                const color = HEALTH_COLORS[t.health] || "#64748b";
                const launch = isLaunch(t);
                const crit = t.is_critical;

                if (t.is_milestone) {
                  const glyph = MS_SHAPES[t.ms_shape] || "▼";
                  const mcolor = HEALTH_COLORS[t.health] || t.ms_color || "#64748b";
                  return (
                    <button key={t.id} onClick={() => onOpenTask?.(t)}
                      className="absolute flex items-center gap-1.5 hover:brightness-110 transition-all text-left"
                      style={{ left: x - 8, top: yTop, minHeight: BAR_H }}
                      data-testid={`swim-item-${t.id}`} title={`${glyph} ${t.title} · ${t.start_date} · ${t.percent_complete || 0}% · ${HEALTH_LABELS[t.health] || ""}`}>
                      {!hideDates && t.start_date && (
                        <span className="absolute right-full mr-1.5 tabular-nums font-semibold whitespace-nowrap" style={{ fontSize: Math.max(9, fontSize - 2), color: fontColor || "#64748b", top: "50%", transform: "translateY(-50%)" }} data-testid={`swim-ms-date-${t.id}`}>
                          {format(parseISO(t.start_date), "MM/dd")}
                        </span>
                      )}
                      {launch
                        ? <Star className="h-4 w-4 shrink-0" style={{ color: mcolor, fill: mcolor }} />
                        : <span className="text-base leading-none shrink-0" style={{ color: mcolor }}>{glyph}</span>}
                      <span className="font-semibold leading-tight line-clamp-2" style={{ color: crit ? "#EF4444" : (fontColor || undefined), fontSize, maxWidth: labelWrap }}>
                        {t.title} <span className="text-muted-foreground">· {t.percent_complete || 0}%</span>{crit && <span className="text-[9px] font-bold text-red-500">★crit</span>}
                        {storyIds[t.id] && <span className="ml-1 font-mono text-[9px] font-bold text-violet-700 bg-violet-100 rounded px-1 align-middle whitespace-nowrap" data-testid={`swim-story-${t.id}`}>{storyIds[t.id].join(" ")}</span>}
                      </span>
                    </button>
                  );
                }
                const w = Math.max(len * pxPerDay - 3, 14);
                const pct = t.percent_complete || 0;
                const estText = (t.title || "").length * fontSize * 0.62 + 20 + 30 + (launch ? 16 : 0);
                const fitsInside = w >= estText;
                return (
                  <Fragment key={t.id}>
                  <button onClick={() => onOpenTask?.(t)}
                    className="absolute rounded-md flex items-center gap-1 px-2 font-medium text-white overflow-hidden hover:brightness-110 transition-all shadow-sm"
                    style={{ left: x, width: w, top: yTop, height: BAR_H, background: color, fontSize, outline: crit ? "2px solid #DC2626" : "none", outlineOffset: "-1px" }}
                    data-testid={`swim-item-${t.id}`}
                    title={`${crit ? "★ CRITICAL · " : ""}${t.title} · ${t.start_date} → ${t.end_date} · ${pct}%`}>
                    {launch && fitsInside && <Star className="h-3 w-3 shrink-0" style={{ fill: "#fff" }} />}
                    {fitsInside && <span className="truncate">{(t.status === "done" || pct >= 100) ? <span data-testid={`swim-done-${t.id}`}>✓ </span> : ""}{t.title}</span>}
                    {fitsInside && storyIds[t.id] && <span className="shrink-0 font-mono text-[9px] font-bold bg-white/30 rounded px-1" data-testid={`swim-story-${t.id}`}>{storyIds[t.id].join(" ")}</span>}
                    {fitsInside && <span className="ml-auto shrink-0 font-bold text-white/90">{pct}%</span>}
                  </button>
                  {!fitsInside && (
                    <button onClick={() => onOpenTask?.(t)}
                      className="absolute flex items-center text-left hover:underline underline-offset-2"
                      style={{ left: x + w + 5, top: yTop, minHeight: BAR_H }}
                      data-testid={`swim-label-${t.id}`}
                      title={`${crit ? "★ CRITICAL · " : ""}${t.title} · ${t.start_date} → ${t.end_date} · ${pct}%`}>
                      <span className="font-semibold leading-tight line-clamp-2" style={{ color: crit ? "#EF4444" : (fontColor || "#1e293b"), fontSize, maxWidth: labelWrap }}>
                        {launch && <Star className="inline h-3 w-3 mr-0.5 align-[-2px]" style={{ color, fill: color }} />}
                        {(t.status === "done" || pct >= 100) && <span className="text-emerald-500 font-bold" data-testid={`swim-done-${t.id}`}>✓ </span>}{t.title} <span className="font-bold" style={{ color: fontColor || color }}>· {pct}%</span>
                        {storyIds[t.id] && <span className="ml-1 font-mono text-[9px] font-bold text-violet-700 bg-violet-100 rounded px-1 align-middle whitespace-nowrap" data-testid={`swim-story-${t.id}`}>{storyIds[t.id].join(" ")}</span>}
                      </span>
                    </button>
                  )}
                  </Fragment>
                );
              }))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 p-3 border-t border-border text-xs bg-secondary/20">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-5 rounded-sm bg-slate-500" /> Task bar</span>
        <span className="inline-flex items-center gap-1.5"><span className="text-base leading-none">▼</span> Milestone</span>
        <span className="inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" style={{ color: "#F59E0B", fill: "#F59E0B" }} /> Launch / key deliverable (colored by health)</span>
        <span className="text-muted-foreground">·</span>
        {["blue", "green", "yellow", "red", "gray"].map((k) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: HEALTH_COLORS[k] }} />{HEALTH_LABELS[k]}</span>
        ))}
      </div>
    </div>
  );
}
